import type { ReactNode } from 'react';
import {
	ContestPerformanceCardWrapper,
	ContestPerformanceCardTitle,
	ContestPerformanceCardDate,
	ContestPerformanceCardHeader,
	ContestPerformanceCardName,
	ContestPerformanceCardContent,
	ContestPerformanceCardContentWrapper,
	ContestPerformanceCardContentValue,
	ScheduledLabelWrapper,
} from './contestPerformanceCard.style';

import { LinkButton } from '../../../button/LinkButton';
import { ProgressBar } from '../progressBar/ProgressBar';
import { PodiumAvatar } from '../../../podiumAvatar/PodiumAvatar';
import { Label } from '../../../label/Label';
import type { LabelVariant } from '../../../label/LabelVariant';
  
export type ContestStatus = 'scheduled' | 'active' | 'completed';
  
export type ContestData = {
	name: string;
	avatarUrl: string;
	placement: number;
	value: number;
	target: number;
	targettype?: string;
	contestType: string;
  
	status?: ContestStatus;
	startsText?: string;
	hideProgress?: boolean;
  };
  
export type ContestPerformanceCardProps = {
	title: string;
	link: string;
	linkText: string;
	start_date: string;
	end_date: string;
	contestData: ContestData;
	children?: ReactNode;
  };
  
const formatShortDate = (isoDate: string): string => {
	if (!isoDate) return '';
	const date = new Date(`${isoDate}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) return isoDate;
  
	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	}).format(date);
};
  
const parseIsoDayUTC = (isoDate: string): Date | null => {
	if (!isoDate) return null;
	const d = new Date(`${isoDate}T00:00:00Z`);
	return Number.isNaN(d.getTime()) ? null : d;
};
  
const getContestPhase = (status: ContestStatus | undefined, start: string, end: string): ContestStatus => {
	if (status) return status;
  
	const startDate = parseIsoDayUTC(start);
	const endDate = parseIsoDayUTC(end);
  
	if (!startDate || !endDate) return 'active';
  
	const now = new Date();
	const nowUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  
	if (nowUtc < startDate) return 'scheduled';
	if (nowUtc > endDate) return 'completed';
	return 'active';
};
  
const getPhaseLabel = (phase: ContestStatus, start: string, end: string, startsText?: string) => {
	switch (phase) {
	case 'scheduled':
		return `${startsText || 'Starts'} ${formatShortDate(start)}`;
	case 'completed':
		return `Ended ${formatShortDate(end)}`;
	case 'active':
	default:
		return 'In Progress';
	}
};
  
const getPhaseVariant = (phase: ContestStatus): LabelVariant => {
	if (phase === 'scheduled') return 'informational';
	if (phase === 'completed') return 'neutral';
	return 'warning'; // active
};

const formatDateRange = (start: string, end: string): string => {
	const s = formatShortDate(start);
	const e = formatShortDate(end);
  
	if (s && e) return `${s} – ${e}`;
	if (s) return s;
	if (e) return e;
	return '';
};
  
const CUSTOM_AVATAR_CONTEST_TYPES = ['jungle', 'mountain', 'space'];

export const ContestPerformanceCard = ({
	title,
	link,
	linkText,
	start_date,
	end_date,
	contestData,
	children,
}: ContestPerformanceCardProps) => {
	const phase = getContestPhase(contestData.status, start_date, end_date);
  
	const phaseLabel = getPhaseLabel(phase, start_date, end_date, contestData.startsText);
	const phaseVariant = getPhaseVariant(phase);
  
	const isScheduled = phase === 'scheduled';
  
	return (
		<ContestPerformanceCardWrapper
			$contestType={contestData.contestType}
		>
			<ScheduledLabelWrapper>
				<Label variant={phaseVariant} size="large" rounded>
					{phaseLabel}
				</Label>
			</ScheduledLabelWrapper>
  
			<ContestPerformanceCardHeader>
				<ContestPerformanceCardTitle>{title}</ContestPerformanceCardTitle>
			</ContestPerformanceCardHeader>
  
			<ContestPerformanceCardDate>
				{formatDateRange(start_date, end_date)}
				{!isScheduled ? (
					<LinkButton
						href={link}
						variant="link"
						size="small"
						iconPosition="right"
						label={linkText}
					/>
				) : null}
			</ContestPerformanceCardDate>
  
			{!isScheduled ? (
				<ContestPerformanceCardContentWrapper>
					{CUSTOM_AVATAR_CONTEST_TYPES.includes(contestData.contestType) && children ? (
						children
					) : (
						<PodiumAvatar
							name={contestData.name}
							avatarUrl={contestData.avatarUrl}
							placement={contestData.placement}
							showSparkles={contestData.placement === 1}
						/>
					)}

					<ContestPerformanceCardContent>
						<ContestPerformanceCardName>{contestData.name}</ContestPerformanceCardName>
  
						{!contestData.hideProgress ? (
							<ProgressBar
								value={contestData.value}
								target={contestData.target}
								targettype={contestData.targettype}
								inline={false}
								useDynamicColors
								showPercent
								showValueLabel
							/>
						) : (
							<ContestPerformanceCardContentValue>
								{contestData.value}
								{contestData.targettype ? ` / ${contestData.targettype}` : ''}
							</ContestPerformanceCardContentValue>
						)}
					</ContestPerformanceCardContent>
				</ContestPerformanceCardContentWrapper>
			) : null}
		</ContestPerformanceCardWrapper>
	);
};
